import {
  MessageCircle,
  MessageSquare,
  PhoneIncoming,
  PhoneOutgoing,
  Send,
  Slack,
  Workflow,
} from 'lucide-react'
import type { FlowTypes } from 'v2/types'
import { panelIconClass } from './config/panel.config'
import { FlowNodeTypes } from './types'

export const nodeIdRegex = /^\{\{([^.]+)/

export enum VarGroups {
  STATIC_VALUES = 'STATIC_VALUES',
  DEFAULT_SENDER_IDS = 'DEFAULT_SENDER_IDS',
}

export const VOICEMAIL_DETECTED_INTENT = {
  name: 'Voicemail Detected',
  instructions: 'When voicemail is detected',
}

// The chat AI node's reply-timeout exit port. Must match the backend's state
// name exactly ("no reply", lowercase with a space) — the runner advances the
// flow with that action string when the customer stays silent past the
// configured reply timeout.
export const NO_REPLY_HANDLE = 'no reply'

export const userIdleConfig = {
  reminder_msg: "Are you still there? I'm happy to assist whenever you're ready",
  hangup_msg:
    "Since I'm not hearing a response, I'll go ahead and disconnect the call. If you need further assistance, please don't hesitate to contact us again.",
  idling_timeout: 15,
  hold_timeout: 30,
  retries: 3,
}

export interface FlowTriggerConfig {
  class: string
  icon: React.ReactNode
  title: string
  description: string
}

export const FlowTriggerConfigMap: Record<FlowTypes, FlowTriggerConfig> = {
  incoming_call: {
    class: panelIconClass.call,
    icon: <PhoneIncoming size={12} className="text-brand-text" />,
    title: 'Voice Call',
    description: 'Triggers when an incoming call is received on your number',
  },
  incoming_chat: {
    class: panelIconClass.chat,
    icon: <MessageSquare size={12} className="text-brand-text" />,
    title: 'Chat Message',
    description: 'Triggers when a new message is received via chat',
  },
  incoming_sms: {
    class: panelIconClass.message,
    icon: <MessageCircle size={12} className="text-brand-text" />,
    title: 'SMS',
    description: 'Triggers when a new SMS is received on your number',
  },
  incoming_whatsapp: {
    class: panelIconClass.whatsapp,
    icon: <Send size={12} className="text-brand-text" />,
    title: 'WhatsApp Message',
    description: 'Triggers when a new WhatsApp message is received',
  },
  outgoing_call: {
    class: panelIconClass.call,
    icon: <PhoneOutgoing size={12} className="text-brand-text" />,
    title: 'When a call is made',
    description: 'Triggers when an outbound call is initiated',
  },
  outgoing_sms: {
    class: panelIconClass.message,
    icon: <MessageCircle size={12} className="text-brand-text" />,
    title: 'When a SMS is sent',
    description: 'Triggers when an SMS is sent',
  },
  outgoing_whatsapp: {
    class: panelIconClass.whatsapp,
    icon: <Send size={12} className="text-brand-text" />,
    title: 'When a WhatsApp message is sent',
    description: 'Triggers when a WhatsApp message is sent',
  },
  api_request: {
    class: panelIconClass.logic,
    icon: <Workflow size={12} className="text-brand-text" />,
    title: 'API Request',
    description: 'Triggers when a HTTP request is made to the specified URL',
  },
  incoming_slack: {
    class: panelIconClass.logic,
    icon: <Slack size={12} className="text-brand-text" />,
    title: 'Slack',
    description: 'Triggers when a specific event occurs in your Slack workspace',
  },
}

export const FlowNodeDescriptions: Record<FlowNodeTypes, string> = {
  [FlowNodeTypes.start]: 'Starting point of the flow',
  [FlowNodeTypes.agent_preset_conversation]:
    'Sends a predefined SMS/MMS, WhatsApp, or chat message to the customer',
  [FlowNodeTypes.end_conversation]: 'Ends the current conversation',
  [FlowNodeTypes.note]: 'Adds a note to the flow',
  [FlowNodeTypes.send_whatsapp]:
    'Initiates a WhatsApp conversation with the customer using a predefined template.',
  [FlowNodeTypes.initiate_call]: 'Triggers an outbound call to a specified number',
  [FlowNodeTypes.send_message]: 'Starts a new SMS conversation with the customer',
  [FlowNodeTypes.ai_agent_v2]: 'Understands customer queries and extracts key details',
  [FlowNodeTypes.ai_agent_v29]:
    'Talks freely and hands every value to a specialist collector that verifies it',
  [FlowNodeTypes.agent_node]:
    'Collects a list of variables over a call. Each variable becomes a guided step',
  [FlowNodeTypes.contact_screening]:
    'Confirms who answered the call and routes on the screening outcome',
  [FlowNodeTypes.outbound_screening]:
    'Places an outbound call and screens who answered — dial and screening in one node',
  [FlowNodeTypes.ai_action]: "Executes actions based on the user's intent",
  [FlowNodeTypes.ai_assist]: 'Route this conversation to a human agent for continued support',
  [FlowNodeTypes.business_hour]:
    'Checks if the current time falls within your configured working hours',
  [FlowNodeTypes.create_task]: 'Creates a task or follow-up',
  [FlowNodeTypes.http_request]:
    'Sends an API request to an external system and processes the response',
  [FlowNodeTypes.ivr_menu]:
    'Presents a voice menu for callers to select options using keypad input',
  [FlowNodeTypes.get_input]: 'Captures DTMF input (key presses)',
  [FlowNodeTypes.call_forward]: 'Transfers the active call to another phone number or agent',
  [FlowNodeTypes.hangup]: 'Ends the current call session with the customer',
  [FlowNodeTypes.voicemail]: 'Lets the caller leave a voicemail which is recorded and stored',
  [FlowNodeTypes.prompt]: 'Plays a pre-recorded audio message to the caller during the call',
  [FlowNodeTypes.customer_feedback]:
    'Collects post-interaction feedback from the customer via call or message',
  [FlowNodeTypes.user_input]:
    'Gathers structured inputs like name, email, or phone number from the user',
  [FlowNodeTypes.branch_v2]: 'Routes the flow based on defined conditions or data values',
  [FlowNodeTypes.counter]: 'Tracks the number of times a path is taken or a condition is met',
  [FlowNodeTypes.ai_agent_call]: 'AI agent for handling voice calls',
  [FlowNodeTypes.ai_agent_message]: 'AI agent for handling text messages',
  [FlowNodeTypes.ai_agent_chat]: 'AI agent for handling chat conversations',
  [FlowNodeTypes.ai_agent_whatsapp]: 'AI agent for handling WhatsApp conversations',
  [FlowNodeTypes.send_chat]: 'Sends a chat message to the customer',
  [FlowNodeTypes.multi_party_call_outbound]:
    'Initiates a voice call to the customer using automated or live voice options',
  [FlowNodeTypes.whatsapp_conversation]:
    'Initiates a WhatsApp conversation with the customer using a predefined template.',
  [FlowNodeTypes.message_conversation]: 'Starts a new SMS conversation with the customer',
  [FlowNodeTypes.app_action]: 'Executes application-specific actions',
  [FlowNodeTypes.queue_and_route]: 'Route this conversation to a human agent for continued support',
  [FlowNodeTypes.menu]: 'Presents menu options to the user',
  [FlowNodeTypes.select_trigger]: 'Allows selection of trigger conditions',
  [FlowNodeTypes.stream]: 'Streams audio data to external services for real-time processing',
  [FlowNodeTypes.ai_skills]:
    'Applies AI to analyze and generate insights from text, audio, image, or video inputs.',
  [FlowNodeTypes.multi_party_call_v2]:
    'Manages multi-party conference calls with agents, customers, and supervisors',
  [FlowNodeTypes.conference_bridge]:
    'Creates a conference bridge for multiple participants to join',
}

export const DEFAULT_LLM_MODEL_ID = 'gpt-4.1-mini'
export const DEFAULT_LLM_PROVIDER = 'openai'

// Node types that have channel configurations
// These are used for channel-based updates in flow operations
export const NODE_TYPES_WITH_CHANNEL = [
  FlowNodeTypes.agent_preset_conversation,
  FlowNodeTypes.ai_agent_v2,
  FlowNodeTypes.ai_agent_v29,
] as readonly FlowNodeTypes[]

export const DEFAULT_TEXT_SUMMARIZATION_INSTRUCTIONS = [
  'You are an expert assistant. Summarize the following text clearly and concisely.',
  '',
  'Focus on extracting the most important points, main ideas, and key takeaways.',
  'Maintain the original meaning while reducing unnecessary detail or repetition.',
  '',
  'Use a neutral and informative tone.',
  'If the content includes multiple ideas or steps, break the summary into clear bullet points.',
  'Otherwise, summarize in 2–4 well-structured sentences.',
  '',
  'Do not include opinions, assumptions, or unrelated content.',
  'The summary should stand alone and make sense without needing to read the original input.',
].join('\n')

export const SUPPORTED_STT_PROVIDERS = ['deepgram']

// Default values for workflow nodes
export const DEFAULT_QUEUE_NAME = 'Simple Queue'
export const DEFAULT_TEAM_NAME = 'default_team'

export const FLOW_RUN_LOGS_EXPIRY_DAYS = 14

export const DEFAULT_INITIAL_WAIT_TIME = 3

// Agent Flow API URLs
export const AGENT_FLOW_API_URL = {
  prod: 'https://agentflow.plivo.com',
  dev: 'https://dev-runner-service.contactodev.com',
} as const
